import Component from '@glimmer/component'
import { service } from '@ember/service'
import type Store from '@ember-data/store'
import { isPresent } from '@ember/utils'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import { task, timeout } from 'ember-concurrency'
import { waitFor } from '@ember/test-waiters'
import { sharesClassQueryFilter } from 'teacher-ui-client/subscription-type/book-library/shares/index/route'
import type BookLibraryShare from 'teacher-ui-client/models/book-library/share'
import { ShareStatus } from 'teacher-ui-client/models/book-library/share'
import type { SchoolClassOptionTypes } from 'teacher-ui-client/components/base/select/school-class/options'
import type SessionService from 'teacher-ui-client/services/session'
import { t, type IntlService, formatDate } from 'ember-intl'
import type Teacher from 'teacher-ui-client/models/teacher'
import doAction from 'teacher-ui-client/modifiers/do-action'
import perform from 'ember-concurrency/helpers/perform'
import { and, eq } from 'ember-truth-helpers'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { LinkTo } from '@ember/routing'
import { hash, array } from '@ember/helper'
import { NoResultsMessage } from 'teacher-ui-client/components/no-results-message'
import { BasePageSelector } from 'teacher-ui-client/components/base/page-selector'
import FidgetLoadingIndicator from '@blakeelearning/fidget/components/fidget/loading-indicator'
import { UiButtonLinkTo } from 'teacher-ui-client/components/ui-button/link-to'
import { BookLibraryShareStatus } from 'teacher-ui-client/components/book-library/share-status'
import { isNumber } from 'lodash'
import type { PhosphorIconName } from 'phosphor-icons'

interface Args {
  expandedShareId?: string
}

interface Signature {
  Element: HTMLTableElement
  Args: Args
  Blocks: {
    expandedRow: []
  }
}

function getSortIcon(
  { sortable, dataKey }: { sortable?: boolean; dataKey?: string },
  sortBy: string,
): PhosphorIconName | undefined {
  if (!sortable) return
  switch (sortBy) {
    case `${dataKey}`:
      return 'phosphor-caret-up-fill'
    case `-${dataKey}`:
      return 'phosphor-caret-down-fill'
    default:
      return 'phosphor-caret-up-down-fill'
  }
}

function calculateSortBy({ sortable, dataKey }: { sortable?: boolean; dataKey?: string }, sortBy: string) {
  if (!sortable) return
  switch (sortBy) {
    case `${dataKey}`:
      return `-${dataKey}`
    case `-${dataKey}`:
      return `${dataKey}`
    default:
      return `${dataKey}`
  }
}

export class BookLibrarySharesTable extends Component<Signature> {
  @service store!: Store

  @service session!: SessionService

  @service intl!: IntlService

  @queryParam('subscription-type/book-library/shares/index/by-collection') page!: number

  @queryParam('subscription-type/book-library/shares/index/by-collection') size!: number

  @queryParam('subscription-type/book-library/shares/index/by-collection') sort!: string

  @queryParam('subscription-type/book-library/shares/index/by-collection') status!: ShareStatus[]

  @queryParam('subscription-type/book-library/shares/index') schoolClassId!: string | SchoolClassOptionTypes

  @queryParam('subscription-type/book-library/shares/index') search!: string

  ShareStatus = ShareStatus

  get query() {
    const query: Record<string, unknown> = {
      page: {
        number: this.page,
        size: this.size,
      },
      sort: this.sort,
      filter: {
        status: this.status.join(','),
        ...sharesClassQueryFilter(this.schoolClassId, this.store, this.session),
        ...(isPresent(this.search) ? { 'collection.name': this.search } : {}),
      },
    }
    return query
  }

  queryShares = task(
    { restartable: true },
    waitFor(async (query: Record<string, unknown>) => {
      await timeout(100)
      const shares = await this.store.query<BookLibraryShare>('book-library/share', {
        ...query,
        // @ts-expect-error - include types not correct
        include: 'collection,collection.books,collection.books.book-metadata',
      })
      // Each share has a "shared by" teacher - need their names for the table.
      const uniqueTeacherIds = shares.reduce<string[]>((teacherIds, share) => {
        if (!share.sharedByUserId) return teacherIds
        return teacherIds.includes(share.sharedByUserId) ? teacherIds : [...teacherIds, share.sharedByUserId]
      }, [])
      // Due to the page size, this should be at most 10 different teachers.
      if (!uniqueTeacherIds) return { shares, teachers: [] }
      const teachers = await this.store.query<Teacher>('teacher', { filter: { ids: uniqueTeacherIds } })

      return { shares, teachers }
    }),
  )

  get columns() {
    return [
      { classNames: 'w-12' }, // toggle edit (no label)
      {
        labelKey: 'bookLibrary.components.sharesTable.headers.collectionName',
        dataKey: 'collection.name',
        sortable: true,
        classNames: 'w-2/12',
      },
      {
        labelKey: 'bookLibrary.components.sharesTable.headers.sharedBy',
        dataKey: 'collection.teacher.name',
        classNames: 'w-2/12',
      },
      {
        labelKey: 'bookLibrary.components.sharesTable.headers.startDate',
        dataKey: 'starts_at',
        sortable: true,
        classNames: 'w-2/12',
      },
      {
        labelKey: 'bookLibrary.components.sharesTable.headers.endDate',
        dataKey: 'expires_at',
        sortable: true,
        classNames: 'w-2/12',
      },
      {
        labelKey: 'bookLibrary.components.sharesTable.headers.status',
        dataKey: 'status',
        sortable: true,
        classNames: 'w-1/12',
      },
      {},
    ].map((column) => ({
      sortIcon: getSortIcon(column, this.sort),
      sortBy: calculateSortBy(column, this.sort),
      ...column,
    }))
  }

  /**
   * TODO: lastSuccessful is set both when the task is performed, and when that perform finishes..
   * That results in the rows getter to update twice, causing 2 table renders
   * https://github.com/machty/ember-concurrency/issues/467
   */
  get shares(): BookLibraryShare[] {
    return this.queryShares.lastSuccessful?.value?.shares ?? []
  }

  get shareTeachers(): Teacher[] {
    return this.queryShares.lastSuccessful?.value?.teachers ?? []
  }

  get rows() {
    // maps each share data model to view model properties and places the data
    // model record under the key `record`.
    const { shares, shareTeachers } = this
    return shares.map((share) => {
      const teacher = shareTeachers.find((t) => t.id === share.sharedByUserId)
      let sharedByName = '-'
      if (teacher) {
        sharedByName = teacher.id === this.session.currentTeacher.id ? this.intl.t('me') : teacher.fullName
      }
      return {
        id: share.id,
        sharedByName,
        collectionName: share.collection.name,
        collectionId: share.collection.id,
        record: share,
      }
    })
  }

  <template>
    <table class="divide-dusty-black-100 divide-y" {{doAction (perform this.queryShares this.query)}} ...attributes>
      <thead>
        <tr>
          {{#each this.columns as |col|}}
            {{#if col.sortable}}
              <th data-test-col={{col.dataKey}} class="{{col.classNames}} px-4 py-2 font-normal">
                {{! Ideally would be a QP-only linkto, but we need to specify the route because the user could be on the
            show or edit routes, which require a an ID dynamic segment. }}
                <LinkTo
                  @route="subscription-type.book-library.shares.index.by-collection"
                  @query={{hash sort=col.sortBy}}
                  class="text-dusty-black-250 flex items-center space-x-2"
                >
                  <span>{{t col.labelKey}}</span>
                  {{#if col.sortIcon}}
                    <PhosphorIcon @icon={{col.sortIcon}} />
                  {{/if}}
                </LinkTo>
              </th>
            {{else}}
              <th class="{{col.classNames}} text-dusty-black-250 px-4 py-2 font-normal">{{if
                  col.labelKey
                  (t col.labelKey)
                }}</th>
            {{/if}}
          {{/each}}
        </tr>
      </thead>
      <tbody class="divide-dusty-black-100 divide-y {{if this.queryShares.isRunning 'opacity-50'}}">
        {{#each this.rows as |row|}}
          {{#let (eq @expandedShareId row.id) as |isExpanded|}}
            <tr data-test-share-row-id={{row.id}}>
              <td class="pl-6">
                {{#if isExpanded}}
                  <LinkTo @route="subscription-type.book-library.shares.index.by-collection">
                    <PhosphorIcon @icon="phosphor-caret-right-bold" class="text-dusty-black-250 h-8 w-8 rotate-90" />
                  </LinkTo>
                {{else}}
                  <LinkTo
                    @route="subscription-type.book-library.shares.index.by-collection.show"
                    @models={{array row.id}}
                  >
                    <PhosphorIcon @icon="phosphor-caret-right-bold" class="text-dusty-black-250 h-8 w-8" />
                  </LinkTo>
                {{/if}}
              </td>
              <td class="px-4 py-2 font-bold" data-test-cell="collection-name">
                {{row.collectionName}}
              </td>
              <td class="px-4 py-2" data-test-cell="created-by">
                {{row.sharedByName}}
              </td>
              <td class="px-4 py-2" data-test-cell="start-date">
                {{formatDate row.record.startsAt weekday="short" day="2-digit" month="short" year="2-digit"}}
              </td>
              <td class="px-4 py-2" data-test-cell="end-date">
                {{formatDate row.record.expiresAt weekday="short" day="2-digit" month="short" year="2-digit"}}
              </td>
              <td class="px-4 py-2 whitespace-nowrap" data-test-cell="status">
                <BookLibraryShareStatus class="px-2 py-1 text-sm" @share={{row.record}} />
              </td>
              <td class="space-x-4 px-4 py-2 text-right whitespace-nowrap" data-test-cell="actions">
                {{#unless (eq row.record.status this.ShareStatus.Expired)}}
                  {{#if isExpanded}}
                    <UiButtonLinkTo
                      data-test-close-share-link
                      @route="subscription-type.book-library.shares.index.by-collection"
                    >
                      <PhosphorIcon @icon="phosphor-x-bold" class="text-dusty-black-250 pr-1" />
                      {{t "bookLibrary.components.sharesTable.editClose"}}
                    </UiButtonLinkTo>
                  {{else}}
                    <UiButtonLinkTo
                      data-test-edit-share-link
                      @route="subscription-type.book-library.shares.index.by-collection.edit"
                      @models={{array row.id}}
                    >
                      <PhosphorIcon @icon="phosphor-note-pencil-fill" class="text-dusty-black-250 pr-1" />
                      {{t "bookLibrary.components.sharesTable.editOpen"}}
                    </UiButtonLinkTo>
                  {{/if}}
                {{/unless}}
                <UiButtonLinkTo
                  data-test-view-collection-link
                  @route="subscription-type.book-library.collection.show"
                  @models={{array row.collectionId}}
                  target="_blank"
                >
                  <PhosphorIcon @icon="phosphor-arrow-square-out-bold" class="text-dusty-black-250 pr-1" />
                  {{t "bookLibrary.components.sharesTable.goToCollection"}}
                </UiButtonLinkTo>
              </td>
            </tr>
            {{#if (and isExpanded (has-block "expandedRow"))}}
              <tr data-test-share-expanded-row-id={{row.id}}>
                <td colspan={{this.columns.length}} class="py-4 pr-4 pl-16">
                  {{yield to="expandedRow"}}
                </td>
              </tr>
            {{/if}}
          {{/let}}
        {{else}}
          <tr>
            <td colspan={{this.columns.length}} class="p-4 text-center">
              {{#if this.queryShares.lastSuccessful}}
                <NoResultsMessage />
              {{else}}
                <FidgetLoadingIndicator
                  data-test-shares-table-loading
                  @show={{true}}
                  @centered={{true}}
                  @appearance="small"
                  @overlay={{false}}
                />
              {{/if}}
            </td>
          </tr>
        {{/each}}
      </tbody>
      {{#if (isNumber this.queryShares.lastSuccessful.value.shares.meta.total-pages)}}
        {{#if (isNumber this.queryShares.lastSuccessful.value.shares.meta.total)}}
          <tfoot>
            <tr>
              <td colspan={{this.columns.length}}>
                <BasePageSelector
                  class="my-4"
                  @pageCount={{this.queryShares.lastSuccessful.value.shares.meta.total-pages}}
                  @pageNumberKey="page"
                  @itemCount={{this.queryShares.lastSuccessful.value.shares.meta.total}}
                />
              </td>
            </tr>
          </tfoot>
        {{/if}}
      {{/if}}
    </table>
  </template>
}

export default BookLibrarySharesTable
